import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../App';
import { toast } from 'sonner';
import { 
  ChevronLeft, ChevronRight, Save, LogOut, MessageCircle,
  CheckCircle, CheckCircle2, Building2, Leaf, BarChart3, Target, HelpCircle
} from 'lucide-react';

import { AIAdvisor } from '../components/questionnaire/AIAdvisor';
import { ComplianceGate } from '../components/ComplianceGate';
import { SectionA } from '../components/questionnaire/SectionA';
import { SectionB } from '../components/questionnaire/SectionB';
import { SectionC } from '../components/questionnaire/SectionC';
import { SectionD } from '../components/questionnaire/SectionD';
import { buildFindings, ConsistencyCheckModal } from '../components/questionnaire/ConsistencyCheck';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

const SECTIONS = [
  { id: 'a', title: 'Organisational Profile', icon: Building2, subtitle: 'Provide context about your organization for comparability' },
  { id: 'b', title: 'Double Materiality Assessment', icon: BarChart3, subtitle: 'Rate each ESG issue on financial relevance and impact' },
  { id: 'c', title: 'Quantitative KPI Inputs', icon: Target, subtitle: "Enter your organization's quantitative KPI data" },
  { id: 'd', title: 'Forward-Looking Insights', icon: Leaf, subtitle: 'Share your forward-looking ESG insights and targets' }
];

export default function QuestionnairePage() {
  const [currentSection, setCurrentSection] = useState(0);
  const [data, setData] = useState({
    section_a: { business_segments: [] },
    section_b: {},
    section_c: {},
    section_d: { top_risks: [] }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdvisor, setShowAdvisor] = useState(false);
  const [responseCode, setResponseCode] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [complianceAccepted, setComplianceAccepted] = useState(null);
  const [checkFindings, setCheckFindings] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadQuestionnaire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuestionnaire = async () => {
    try {
      const [res, comp] = await Promise.all([
        api.get('/api/questionnaire'),
        api.get('/api/compliance/status')
      ]);
      setComplianceAccepted(comp.data.accepted);
      if (res.data) {
        setData({
          section_a: { 
            ...res.data.section_a,
            business_segments: res.data.section_a?.business_segments || []
          },
          section_b: res.data.section_b || {},
          section_c: res.data.section_c || {},
          section_d: {
            ...res.data.section_d,
            top_risks: res.data.section_d?.top_risks || []
          }
        });
        setResponseCode(res.data.response_code || null);
        setIsSubmitted(!!res.data.is_complete);
      }
    } catch (error) {
      console.error('Error loading questionnaire:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const section_d = { ...data.section_d };
    if (section_d.has_esg_targets === false) {
      section_d.carbon_target_2030 = null;
    }
    return {
      section_a: data.section_a || {},
      section_b: data.section_b || {},
      section_c: data.section_c || {},
      section_d
    };
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      await api.post('/api/questionnaire/save', { ...buildPayload(), is_complete: false });
      toast.success(isSubmitted ? 'Changes saved — edits are logged against your original response' : 'Progress saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const submitQuestionnaire = async () => {
    setSaving(true);
    try {
      const res = await api.post('/api/questionnaire/submit', { ...buildPayload(), is_complete: true });
      const code = res.data.response_code;
      setResponseCode(code || null);
      setIsSubmitted(true);
      toast.success(
        isSubmitted
          ? 'Response updated! Your changes have been logged.'
          : `Questionnaire submitted! Your Response Code is ${code}`
      );
      navigate('/comparison');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit questionnaire');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitClick = () => {
    const findings = buildFindings(data);
    if (findings.length === 0) {
      submitQuestionnaire();
    } else {
      setCheckFindings(findings);
    }
  };

  const updateSection = (section, field, value) => {
    setData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const handleESGTargetsChange = (hasTargets) => {
    setData(prev => ({
      ...prev,
      section_d: {
        ...prev.section_d,
        has_esg_targets: hasTargets,
        carbon_target_2030: hasTargets ? prev.section_d.carbon_target_2030 : null
      }
    }));
  };

  const toggleBusinessSegment = (segment) => {
    setData(prev => {
      const current = prev.section_a.business_segments || [];
      const updated = current.includes(segment)
        ? current.filter(s => s !== segment)
        : [...current, segment];
      return { ...prev, section_a: { ...prev.section_a, business_segments: updated } };
    });
  };

  const handleRiskSelection = (risk) => {
    setData(prev => {
      const current = prev.section_d.top_risks || [];
      if (current.includes(risk)) {
        return { ...prev, section_d: { ...prev.section_d, top_risks: current.filter(r => r !== risk) } };
      }
      if (current.length < 3) {
        return { ...prev, section_d: { ...prev.section_d, top_risks: [...current, risk] } };
      }
      return prev;
    });
  };

  const resetRiskSelection = () => {
    setData(prev => ({ ...prev, section_d: { ...prev.section_d, top_risks: [] } }));
  };

  const getRiskRank = (risk) => {
    const index = (data.section_d.top_risks || []).indexOf(risk);
    return index >= 0 ? index + 1 : null;
  };

  const progress = ((currentSection + 1) / SECTIONS.length) * 100;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (complianceAccepted === false) {
    return <ComplianceGate onAccepted={() => setComplianceAccepted(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain" />
            <div className="hidden md:block">
              <p className="text-sm text-slate-500">Welcome,</p>
              <p className="font-medium text-primary">{user?.company_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/guide"
              className="text-slate-500 hover:text-primary p-2"
              title="User Guide"
              data-testid="guide-link"
            >
              <HelpCircle className="w-5 h-5" />
            </Link>
            <Link
              to="/comparison"
              className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              data-testid="view-comparison-btn"
            >
              <BarChart3 className="w-4 h-4" />
              <span className="hidden md:inline">View Report</span>
            </Link>
            <button
              onClick={saveProgress}
              disabled={saving}
              className="btn-secondary h-10 px-4 flex items-center gap-2 text-sm"
              data-testid="save-progress-btn"
            >
              <Save className="w-4 h-4" />
              <span className="hidden md:inline">Save Progress</span>
            </button>
            <button
              onClick={logout}
              className="text-slate-500 hover:text-slate-700 p-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div 
          className="h-1 bg-emerald-500 transition-all duration-300" 
          style={{ width: `${progress}%` }}
        />
      </header>

      {/* Main Content */}
      <main className="px-4 md:px-8 lg:px-16 py-8 max-w-5xl mx-auto">
        {/* Submitted banner */}
        {isSubmitted && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex flex-wrap items-center gap-3" data-testid="submitted-banner">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm font-medium text-emerald-800">
                Response submitted{responseCode && <> — Response Code: <code className="font-mono font-bold">{responseCode}</code></>}
              </p>
              <p className="text-xs text-emerald-600 mt-0.5">
                You may edit your answers. All changes are logged against your original submission.
              </p>
            </div>
          </div>
        )}

        {/* Section Navigation */}
        <div className="flex overflow-x-auto gap-2 pb-4 mb-8">
          {SECTIONS.map((section, idx) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setCurrentSection(idx)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  currentSection === idx 
                    ? 'bg-accent text-primary' 
                    : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
                data-testid={`section-nav-${section.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">Section {section.id.toUpperCase()}</span>
              </button>
            );
          })}
        </div>

        {/* Section Content */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 animate-fade-in">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-2">
              {SECTIONS[currentSection].title}
            </h2>
            <p className="text-slate-500">{SECTIONS[currentSection].subtitle}</p>
          </div>

          {currentSection === 0 && (
            <SectionA data={data} updateSection={updateSection} toggleBusinessSegment={toggleBusinessSegment} />
          )}
          {currentSection === 1 && (
            <SectionB data={data} updateSection={updateSection} />
          )}
          {currentSection === 2 && (
            <SectionC data={data} updateSection={updateSection} />
          )}
          {currentSection === 3 && (
            <SectionD
              data={data}
              updateSection={updateSection}
              handleESGTargetsChange={handleESGTargetsChange}
              handleRiskSelection={handleRiskSelection}
              resetRiskSelection={resetRiskSelection}
              getRiskRank={getRiskRank}
            />
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <button
              onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
              disabled={currentSection === 0}
              className="flex items-center gap-2 text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="prev-section-btn"
            >
              <ChevronLeft className="w-5 h-5" />
              Previous
            </button>

            {currentSection < SECTIONS.length - 1 ? (
              <button
                onClick={() => setCurrentSection(prev => prev + 1)}
                className="btn-accent flex items-center gap-2"
                data-testid="next-section-btn"
              >
                Next Section
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmitClick}
                disabled={saving}
                className="btn-accent flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600"
                data-testid="submit-questionnaire-btn"
              >
                <CheckCircle className="w-5 h-5" />
                Submit Questionnaire
              </button>
            )}
          </div>
        </div>
      </main>

      {/* AI Advisor Toggle Button */}
      <button
        onClick={() => setShowAdvisor(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-accent text-primary rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all animate-pulse-glow z-40"
        data-testid="open-advisor-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      <AIAdvisor isOpen={showAdvisor} onClose={() => setShowAdvisor(false)} />

      {checkFindings && (
        <ConsistencyCheckModal
          findings={checkFindings}
          saving={saving}
          onClose={() => setCheckFindings(null)}
          onGoToSection={(idx) => {
            setCurrentSection(idx);
            setCheckFindings(null);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onSubmitAnyway={() => {
            setCheckFindings(null);
            submitQuestionnaire();
          }}
        />
      )}

      {/* Footer */}
      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a 
          href="https://sustek.ai/solutions/soctech" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-emerald-600 hover:text-emerald-700 hover:underline"
        >
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
